import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { catchError, forkJoin, map, of } from 'rxjs';

import { ButtonComponent } from '@libs/front/components/button/button.component';
import { InputComponent } from '@libs/front/components/input/input.component';

import { DirectoryType } from 'src/app/bo/fichier';
import { Tir } from 'src/app/bo/tir';
import { BlastService } from 'src/app/services/blast.service';
import { DocumentStorageService } from 'src/app/services/document-storage.service';
import { FonctionsService } from 'src/app/services/fonctions-partagees.service';
import { BlastJobDto, GeneralService, ImportStatus } from 'src/app/services/general.service';
import { UtilisateurService } from 'src/app/services/utilisateur.service';

type HistoryStatus = 'status-success' | 'status-partial' | 'status-error' | 'status-unknown';
type HistorySortKey = 'source' | 'user' | 'date' | 'status';

interface HistoryRow {
  job: BlastJobDto;
  source: string;
  filePath: string;
  firstName: string;
  lastName: string;
  user: string;
  date: string;
  timestamp: number;
  status: HistoryStatus;
  errors: string;
  reenabledHoles: boolean;
  canDownload: boolean;
}

const PAGE_SIZE = 20;
const IFRAME_LIFETIME_MS = 60_000;

const STATUS_LABEL_KEYS: Record<HistoryStatus, string> = {
  'status-success': 'BLAST_HISTORY.STATUS_SUCCESS',
  'status-partial': 'BLAST_HISTORY.STATUS_PARTIAL',
  'status-error':   'BLAST_HISTORY.STATUS_ERROR',
  'status-unknown': 'BLAST_HISTORY.STATUS_UNKNOWN',
};

const STATUS_ORDER: Record<HistoryStatus, number> = {
  'status-success': 0,
  'status-partial': 1,
  'status-error':   2,
  'status-unknown': 3,
};

@Component({
  selector: 'app-blast-job-history',
  standalone: true,
  templateUrl: './blast-job-history.component.html',
  styleUrls: ['./blast-job-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TranslateModule, ButtonComponent, InputComponent],
})
export class BlastJobHistoryComponent {
  readonly tirId = input<number>();

  private readonly generalService         = inject(GeneralService);
  private readonly documentStorageService = inject(DocumentStorageService);
  private readonly blastService           = inject(BlastService);
  private readonly utilService            = inject(UtilisateurService);
  private readonly fonctionsService       = inject(FonctionsService);
  private readonly translate              = inject(TranslateService);

  protected readonly statusLabelKeys = STATUS_LABEL_KEYS;
  protected readonly columns: { key: HistorySortKey; label: string }[] = [
    { key: 'source', label: 'BLAST_HISTORY.SOURCE' },
    { key: 'user',   label: 'BLAST_HISTORY.USER' },
    { key: 'date',   label: 'BLAST_HISTORY.DATE' },
    { key: 'status', label: 'BLAST_HISTORY.STATE' },
  ];

  protected readonly loading       = signal(false);
  protected readonly jobs          = signal<BlastJobDto[]>([]);
  protected readonly searchText    = signal('');
  protected readonly sortKey       = signal<HistorySortKey | null>(null);
  protected readonly sortDirection = signal<'asc' | 'desc'>('asc');
  protected readonly pageIndex     = signal(0);
  private siteId: number | null = null;

  protected readonly rows = computed(() => this.jobs().map(job => this.toRow(job)));

  protected readonly filteredRows = computed(() => {
    const term = this.searchText().trim().toLowerCase();
    if (!term) return this.rows();
    return this.rows().filter(row =>
      [row.source, row.user, row.date, row.filePath, this.translate.instant(STATUS_LABEL_KEYS[row.status])]
        .some(value => value.toLowerCase().includes(term))
    );
  });

  protected readonly sortedRows = computed(() => {
    const key = this.sortKey();
    if (!key) return this.filteredRows();
    const factor = this.sortDirection() === 'asc' ? 1 : -1;
    return [...this.filteredRows()].sort((a, b) => factor * this.compareRows(a, b, key));
  });

  protected readonly pageCount   = computed(() => Math.max(1, Math.ceil(this.sortedRows().length / PAGE_SIZE)));
  protected readonly currentPage = computed(() => Math.min(this.pageIndex(), this.pageCount() - 1) + 1);
  protected readonly pagedRows   = computed(() => {
    const start = (this.currentPage() - 1) * PAGE_SIZE;
    return this.sortedRows().slice(start, start + PAGE_SIZE);
  });
  protected readonly downloadableRows = computed(() => this.filteredRows().filter(row => row.canDownload));

  constructor() {
    effect(() => {
      const tirId = this.tirId();
      if (tirId) {
        untracked(() => {
          this.loadHistory(tirId);
          this.resolveSiteId(tirId);
        });
      }
    });
  }

  // ── Chargement ────────────────────────────────────────────────────────────

  protected refresh(): void {
    const tirId = this.tirId();
    if (tirId) this.loadHistory(tirId);
  }

  private loadHistory(tirId: number): void {
    this.loading.set(true);
    this.generalService.getBlastJobHistory(tirId).subscribe({
      next: jobs => {
        this.jobs.set(jobs ?? []);
        this.pageIndex.set(0);
        this.loading.set(false);
      },
      error: error => {
        console.error('Error loading blast job history:', error);
        this.loading.set(false);
      },
    });
  }

  /** Le site sert à signer les URL de téléchargement ; il est lu sur le tir partagé, sinon rechargé. */
  private resolveSiteId(tirId: number): void {
    const current = this.utilService.currentTir;
    if (current?.tirId === tirId) {
      this.siteId = current.siteId;
      return;
    }
    this.blastService.getBlast(tirId).subscribe((tir: Tir) => this.siteId = tir?.siteId ?? null);
  }

  private toRow(job: BlastJobDto): HistoryRow {
    const importStatus = job.importStatus;
    const isStrFile = this.isStrFileSource(importStatus);
    const reEnabledHoles = (importStatus as (ImportStatus & { reEnabledHoles?: unknown[] }) | undefined)?.reEnabledHoles;
    const errors = importStatus?.errors?.join('\n') ?? '';

    return {
      job,
      source:         this.generalService.getSourceTypeDisplay(importStatus),
      filePath:       isStrFile ? importStatus?.filePath ?? '' : '',
      firstName:      job.userFirstName ?? '',
      lastName:       job.userLastName ?? '',
      user:           this.generalService.formatUserName(job),
      date:           this.formatDate(job.startDate),
      timestamp:      this.toUtcDate(job.startDate)?.getTime() ?? 0,
      status:         this.generalService.getStatusClass(importStatus) as HistoryStatus,
      errors:         errors.length > 100 ? errors.substring(0, 100) + '...' : errors,
      reenabledHoles: !!reEnabledHoles?.length,
      canDownload:    isStrFile && !!importStatus?.filePath,
    };
  }

  private isStrFileSource(importStatus?: ImportStatus): boolean {
    if (!importStatus) return false;
    const sourceType = String(importStatus.sourceType).toLowerCase();
    return sourceType === '0' || sourceType === 'strfile';
  }

  /** Les dates du backend sont en UTC sans suffixe : on force l'interprétation UTC avant l'affichage local. */
  private toUtcDate(date: Date | string): Date | null {
    if (!date) return null;
    let dateStr = typeof date === 'string' ? date : date.toISOString();
    if (!dateStr.endsWith('Z') && !dateStr.includes('+')) {
      dateStr += 'Z';
    }
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
  }

  private formatDate(date: Date | string): string {
    const d = this.toUtcDate(date);
    return d ? this.generalService.formatDate(d) : '';
  }

  // ── Recherche / tri / pagination ──────────────────────────────────────────

  protected onSearchChange(value: string): void {
    this.searchText.set(value ?? '');
    this.pageIndex.set(0);
  }

  /** Premier clic : croissant (décroissant pour la date, plus récente d'abord) ; clic suivant : inverse. */
  protected toggleSort(key: HistorySortKey): void {
    if (this.sortKey() === key) {
      this.sortDirection.update(direction => direction === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortKey.set(key);
      this.sortDirection.set(key === 'date' ? 'desc' : 'asc');
    }
    this.pageIndex.set(0);
  }

  private compareRows(a: HistoryRow, b: HistoryRow, key: HistorySortKey): number {
    switch (key) {
      case 'date':   return a.timestamp - b.timestamp;
      case 'status': return STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
      default:       return a[key].localeCompare(b[key]);
    }
  }

  protected previousPage(): void {
    this.pageIndex.set(Math.max(0, this.currentPage() - 2));
  }

  protected nextPage(): void {
    this.pageIndex.set(Math.min(this.pageCount() - 1, this.currentPage()));
  }

  // ── Téléchargement ────────────────────────────────────────────────────────

  protected downloadFile(row: HistoryRow): void {
    if (!row.canDownload) return;

    this.fonctionsService.loading_screen(true);
    this.getDownloadUrl(row).subscribe({
      next: result => {
        if (result?.downloadUrl) window.open(result.downloadUrl, '_blank');
        this.fonctionsService.loading_screen(false);
      },
      error: (error: HttpErrorResponse) => this.handleDownloadError(error.message),
    });
  }

  /**
   * Télécharge tous les fichiers STR de la liste filtrée. Chaque URL est ouverte dans une iframe masquée :
   * contrairement à window.open, les téléchargements multiples ne sont pas bloqués par le navigateur.
   */
  protected downloadAll(): void {
    const rows = this.downloadableRows();
    if (rows.length === 0) {
      this.fonctionsService.launch_toast(false, false, this.translate.instant('BLAST_HISTORY.NO_FILE_TO_DOWNLOAD'));
      return;
    }

    this.fonctionsService.loading_screen(true);
    forkJoin(rows.map(row => this.getDownloadUrl(row, true).pipe(
      map(result => result?.downloadUrl ?? null),
      catchError(() => of(null)),
    ))).subscribe(urls => {
      this.fonctionsService.loading_screen(false);
      urls.filter((url): url is string => !!url).forEach(url => this.downloadInIframe(url));
      if (urls.some(url => !url)) {
        this.fonctionsService.launch_toast(false, false, 'Error downloading file');
      }
    });
  }

  private getDownloadUrl(row: HistoryRow, isDownload = false) {
    const siteId = this.siteId ?? this.utilService.currentTir?.siteId ?? 0;
    return this.documentStorageService.getDownloadUrl(row.filePath, row.job.jobId, siteId, DirectoryType.USERFILES, isDownload);
  }

  private downloadInIframe(url: string): void {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = url;
    document.body.appendChild(iframe);
    setTimeout(() => iframe.remove(), IFRAME_LIFETIME_MS);
  }

  private handleDownloadError(errorMessage: string): void {
    this.fonctionsService.loading_screen(false);
    this.fonctionsService.launch_toast(false, false, errorMessage === 'File not found' ? 'File not found' : 'Error downloading file');
  }
}
